// Dev Console context capture: console/network ring buffer + the structured
// context envelope (context.json) attached to agent sessions.
//
// Privacy model: everything here stays in browser memory until an internal
// user explicitly starts a Dev Console session; every string passes scrub()
// before it can enter a prompt. Network capture records method/URL/status/
// timing only — never headers or bodies.

const MAX_ENTRIES = 50;
const SLOW_MS = 3000;
const ring = [];

// ---- scrubber ------------------------------------------------------------------
// Central redaction applied to every captured string: bearer/JWT-ish tokens,
// emails, and sensitive query-param values.
const SECRET_PARAMS = /([?&](?:token|key|apikey|api_key|auth|signature|session|code)=)[^&#\s]+/gi;
export function scrub(value) {
  return String(value ?? '')
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
    .replace(/eyJ[A-Za-z0-9._-]{20,}/g, '[jwt]')
    .replace(/[A-Za-z0-9+/_-]{40,}/g, '[token]')
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
    .replace(SECRET_PARAMS, '$1[redacted]');
}

function push(entry) {
  ring.push({ ts: Date.now(), ...entry });
  if (ring.length > MAX_ENTRIES) ring.shift();
}

// ---- capture hooks -------------------------------------------------------------
let installed = false;
export function initDevCapture() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  for (const level of ['error', 'warn']) {
    const orig = console[level].bind(console);
    console[level] = (...args) => {
      try {
        const text = args.map((a) => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === 'object') { try { return JSON.stringify(a).slice(0, 300); } catch { return String(a); } }
          return String(a);
        }).join(' ');
        push({ kind: 'console', level, text: scrub(text).slice(0, 500) });
      } catch { /* never break the app from a capture hook */ }
      orig(...args);
    };
  }

  window.addEventListener('error', (e) => {
    push({ kind: 'window-error', text: scrub(`${e.message} @ ${e.filename}:${e.lineno}`).slice(0, 500) });
  });
  window.addEventListener('unhandledrejection', (e) => {
    push({ kind: 'unhandled-rejection', text: scrub(e.reason?.message || String(e.reason)).slice(0, 500) });
  });

  // Failed or slow fetches: method, URL, status, timing — never bodies.
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const started = Date.now();
    const method = (init?.method || (typeof input === 'object' && input?.method) || 'GET').toUpperCase();
    const url = typeof input === 'string' ? input : input?.url || '';
    try {
      const res = await origFetch(input, init);
      const ms = Date.now() - started;
      if (!res.ok || ms > SLOW_MS) {
        push({ kind: 'net', method, url: scrub(url).slice(0, 300), status: res.status, ms });
      }
      return res;
    } catch (err) {
      push({ kind: 'net', method, url: scrub(url).slice(0, 300), status: 0, ms: Date.now() - started, error: scrub(err.message).slice(0, 200) });
      throw err;
    }
  };
}

export const getRingBuffer = () => ring.slice(-MAX_ENTRIES);

// ---- context envelope ------------------------------------------------------------
// Versioned, machine-parseable facts block. Schema documented in AGENTS.md
// ("Dev Console context envelope") — keep the two in sync.
export function buildEnvelope({ element = null } = {}) {
  return {
    schema: 'devconsole/1',
    at: new Date().toISOString(),
    route: scrub(window.location.pathname + window.location.search),
    buildSha: process.env.REACT_APP_GIT_SHA || null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    },
    userAgent: navigator.userAgent,
    ringBuffer: getRingBuffer(),
    element,
  };
}

// Render the envelope as the fenced block appended to a session prompt.
export function envelopeBlock(envelope) {
  return `\n\n---\ncontext.json (structured capture — parse before prose):\n\`\`\`json\n${JSON.stringify(envelope, null, 1)}\n\`\`\``;
}

// ---- report handoff --------------------------------------------------------------
// The element picker stashes its capture here; the Dev Console consumes it on
// mount. sessionStorage keeps it out of long-lived storage.
const REPORT_KEY = 'dev-report';

export function stashReport(report) {
  try {
    sessionStorage.setItem(REPORT_KEY, JSON.stringify(report));
    return true;
  } catch {
    return false; // screenshot too large for the quota — caller falls back
  }
}

export function takeReport() {
  try {
    const raw = sessionStorage.getItem(REPORT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(REPORT_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
